"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import {
  CommunityPostOut, LifecycleStage,
  ContentCategoryStat, ContentCategoryKey,
} from "@/types";
import { AnnouncementMarquee } from "@/components/dashboard/AnnouncementMarquee";
import { useDragScroll } from "@/hooks/useDragScroll";
import { useReadPosts } from "@/hooks/useReadPosts";
import { PostRow } from "@/components/community/PostRow";

type StageFilter = LifecycleStage | "all";
type SortKey = "latest" | "popular" | "view";
type PostTypeFilter = "all" | "free" | "item_tagged" | "poll";

const METRIC_LABEL: Record<string, string> = {
  revenue: "매출",
  qty: "판매량",
  rating: "재방문율",
  other: "지표",
};

const PAGE_SIZE = 20;

export default function CommunityPage() {
  return (
    <Suspense fallback={<p className="text-text-sub text-center py-8">불러오는 중...</p>}>
      <CommunityInner />
    </Suspense>
  );
}

function CommunityInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // 비로그인도 글 목록 보기 OK — useAuthGuard 적용 X. hasHydrated 만 가드
  const { token, isLoggedIn: rawIsLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const isLoggedIn = hasHydrated && rawIsLoggedIn;   // hydration 후에만 true

  // stage 필터칩 제거됨 (사용자 피드백) — URL deep-link 만 backward-compat
  const stage = (searchParams.get("stage") as StageFilter | null) ?? "all";
  const initialSort = (searchParams.get("sort") as SortKey | null) ?? "latest";
  const initialQ = searchParams.get("q") ?? "";
  const initialPostType = (searchParams.get("post_type") as PostTypeFilter | null) ?? "all";

  const initialRegion = searchParams.get("region");
  const initialCategory = searchParams.get("category");
  const initialContentCategory = (searchParams.get("content_category") as ContentCategoryKey | null) ?? null;
  const initialInsight = searchParams.get("has_insight") === "1";
  const mineMode = searchParams.get("mine") === "1";   // #9 마이페이지 → 내 게시글

  const [sort, setSort] = useState<SortKey>(initialSort);
  const [searchInput, setSearchInput] = useState(initialQ);
  const [q, setQ] = useState(initialQ);
  const [postType, setPostType] = useState<PostTypeFilter>(initialPostType);
  // R3-33 S4-D
  const [region, setRegion] = useState<string | null>(initialRegion);
  const [category] = useState<string | null>(initialCategory);
  // 마이그 036 — 글 내용 카테고리 (#3)
  const [contentCategory, setContentCategory] = useState<ContentCategoryKey | null>(initialContentCategory);
  const [hasInsight, setHasInsight] = useState<boolean>(initialInsight);
  const [regions, setRegions] = useState<{ region: string; count: number }[]>([]);
  const [contentCats, setContentCats] = useState<ContentCategoryStat[]>([]);

  const [posts, setPosts] = useState<CommunityPostOut[] | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);   // 마이그 101 — 하이브리드 페이지네이션용
  const { isRead, markRead } = useReadPosts();
  // 2026-05-31 — 회원 메뉴 자동 노출 임계 도달 여부 (셋 다 만족: 가입 30 + 7일 활성 15 + 7일 게시 10)
  const [membersVisible, setMembersVisible] = useState(false);

  // 찬반 sub 필터 — postType='poll' 시만 노출 (2026-05-15)
  const [pollStatus, setPollStatus] = useState<"all" | "open" | "closed">("all");

  // chip 가로 스크롤 — 마우스 drag + 휠 변환 (2026-05-15)
  const filterChipsRef = useDragScroll<HTMLDivElement>();
  const regionChipsRef = useDragScroll<HTMLDivElement>();

  // β-9 후속 — 친구글 피드 탭 (D-2 layered) + 마이그 105 장터(market)
  const initialFeed = (searchParams.get("feed") as "all" | "friends" | "mentions" | "market" | null) ?? "all";
  const [feed, setFeed] = useState<"all" | "friends" | "mentions" | "market">(initialFeed);
  // 마이그 105 — 장터 거래유형 토글 (feed=market 일 때)
  const [marketTrade, setMarketTrade] = useState<"all" | "sell" | "buy">("all");
  // 마이그 105 — 장터 거래단계 토글 (feed=market 일 때)
  const [marketStatus, setMarketStatus] = useState<"all" | "selling" | "reserved" | "sold">("all");

  // 지역 목록 — 피드별 분리 (장터/일반 카운트가 실제 목록과 일치하도록, 마이그 105)
  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get<{ region: string; count: number }[]>("/api/community/regions", {
      headers,
      params: { market: feed === "market" },
    })
      .then((r) => setRegions(r.data)).catch(() => setRegions([]));
  }, [token, feed]);

  // 글 내용 카테고리 목록 (1회 로드)
  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get<ContentCategoryStat[]>("/api/community/content-categories", { headers })
      .then((r) => setContentCats(r.data)).catch(() => setContentCats([]));
  }, [token]);

  // 2026-05-31 — 회원 메뉴 가시성 (임계 도달 시 자동 노출)
  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get<{ visible: boolean }>("/api/community/members/visibility", { headers })
      .then((r) => setMembersVisible(!!r.data?.visible))
      .catch(() => setMembersVisible(false));
  }, [token]);

  // (#6) "지금 뜨는 후기" trending 섹션 제거 — 글 종류 칩 통합 후 중복

  // 피드 조회: filter / sort / q / feed 변경 시 리셋 + 재조회
  useEffect(() => {
    setLoading(true);
    setPage(1);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params: Record<string, string | number | boolean> = { page: 1, size: PAGE_SIZE, sort, feed, ...(feed === "market" ? { ...(marketTrade !== "all" ? { market_trade: marketTrade } : {}), ...(marketStatus !== "all" ? { market_status: marketStatus } : {}) } : {}) };
    if (stage !== "all") params.stage = stage;
    if (feed !== "market" && postType !== "all") params.post_type = postType;
    if (region) params.region = region;
    if (category) params.category = category;
    if (feed !== "market" && contentCategory) params.content_category = contentCategory;
    if (mineMode) params.mine = true;
    if (feed !== "market" && hasInsight) params.has_insight = true;
    if (q.trim()) params.q = q.trim();
    axios
      .get<CommunityPostOut[]>("/api/community/posts", { headers, params })
      .then((r) => {
        setPosts(r.data);
        setHasMore(r.data.length === PAGE_SIZE);
        setTotalCount(parseInt(r.headers["x-total-count"] ?? String(r.data.length), 10));
      })
      .catch(() => {
        setPosts([]);
        setHasMore(false);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [stage, sort, q, postType, region, category, contentCategory, mineMode, hasInsight, feed, marketTrade, marketStatus, token]);

  const loadMore = async () => {
    if (!posts || loadingMore) return;
    setLoadingMore(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const next = page + 1;
      const params: Record<string, string | number | boolean> = { page: next, size: PAGE_SIZE, sort, feed, ...(feed === "market" ? { ...(marketTrade !== "all" ? { market_trade: marketTrade } : {}), ...(marketStatus !== "all" ? { market_status: marketStatus } : {}) } : {}) };
      if (stage !== "all") params.stage = stage;
      if (feed !== "market" && postType !== "all") params.post_type = postType;
      if (region) params.region = region;
      if (category) params.category = category;
      if (feed !== "market" && contentCategory) params.content_category = contentCategory;
      if (mineMode) params.mine = true;
      if (feed !== "market" && hasInsight) params.has_insight = true;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get<CommunityPostOut[]>("/api/community/posts", { headers, params });
      setPosts([...posts, ...r.data]);
      setPage(next);
      setHasMore(r.data.length === PAGE_SIZE);
      setTotalCount(parseInt(r.headers["x-total-count"] ?? String(totalCount), 10));
    } finally {
      setLoadingMore(false);
    }
  };

  /** 페이지 번호 직접 점프 (하이브리드 — 무한스크롤과 별개로 깊은 페이지 직접 이동) */
  const gotoPage = async (target: number) => {
    if (target < 1) return;
    setLoadingMore(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params: Record<string, string | number | boolean> = { page: target, size: PAGE_SIZE, sort, feed, ...(feed === "market" ? { ...(marketTrade !== "all" ? { market_trade: marketTrade } : {}), ...(marketStatus !== "all" ? { market_status: marketStatus } : {}) } : {}) };
      if (stage !== "all") params.stage = stage;
      if (feed !== "market" && postType !== "all") params.post_type = postType;
      if (region) params.region = region;
      if (category) params.category = category;
      if (feed !== "market" && contentCategory) params.content_category = contentCategory;
      if (mineMode) params.mine = true;
      if (feed !== "market" && hasInsight) params.has_insight = true;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get<CommunityPostOut[]>("/api/community/posts", { headers, params });
      setPosts(r.data);
      setPage(target);
      setHasMore(r.data.length === PAGE_SIZE);
      setTotalCount(parseInt(r.headers["x-total-count"] ?? String(totalCount), 10));
      // 페이지 상단으로 scroll
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoadingMore(false);
    }
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(searchInput);
    // URL 반영 (선택적)
    const params = new URLSearchParams();
    if (searchInput.trim()) params.set("q", searchInput.trim());
    if (stage !== "all") params.set("stage", stage);
    if (sort !== "latest") params.set("sort", sort);
    const qs = params.toString();
    router.replace(qs ? `/community?${qs}` : "/community");
  };

  return (
    <div className="pb-28">
      {/* 헤더 + 띠배너 (#10) — 띠배너 ↔ 헤더 사이 약간 공백 (사용자 피드백) */}
      <div className="sticky top-0 z-40">
        <AnnouncementMarquee />
        <div className="h-1 bg-bg" />
        <div className="bg-bg backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-black text-text">{mineMode ? "내 게시글" : "커뮤니티"}</h1>
          {!mineMode && membersVisible && (
            <Link href="/community/members" className="text-2xs text-primary hover:underline">
              회원
            </Link>
          )}
        </div>
      </div>

      {/* 비로그인 안내 — 글 읽기는 OK, 작성·댓글·좋아요는 로그인 */}
      {hasHydrated && !isLoggedIn && (
        <div className="mx-4 mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3 flex items-center gap-2">
          <span className="text-base">👀</span>
          <p className="flex-1 text-2xs text-text-sub leading-snug">
            <span className="text-text font-semibold">읽기 전용 모드</span> — 글 작성·댓글·좋아요는 로그인 후 가능
          </p>
          <Link
            href="/login"
            className="text-2xs font-bold px-2.5 py-1.5 rounded-md bg-primary text-bg shrink-0"
          >
            로그인
          </Link>
        </div>
      )}

      {/* β-9 후속 — 피드 탭 (전체/친구/멘션) */}
      {isLoggedIn && (
        <div className="px-4 mt-3 flex gap-1.5 border-b border-border pb-2">
          {[
            { key: "all" as const, label: "전체" },
            { key: "friends" as const, label: "친구" },
            { key: "mentions" as const, label: "멘션받음" },
            { key: "market" as const, label: "🛒 장터" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFeed(t.key)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${
                feed === t.key
                  ? "bg-primary text-bg"
                  : "bg-bg-secondary text-text-sub hover:bg-bg-elevated"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* 마이그 105 — 장터 거래유형 토글 (feed=market 일 때) */}
      {isLoggedIn && feed === "market" && (
        <div className="px-4 mt-2 flex gap-1.5">
          {[
            { key: "all" as const, label: "전체" },
            { key: "sell" as const, label: "팝니다" },
            { key: "buy" as const, label: "삽니다" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setMarketTrade(t.key)}
              className={`text-2xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                marketTrade === t.key ? "bg-primary/10 border-primary text-primary" : "border-border text-text-sub"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* 마이그 105 — 장터 거래단계 토글 (feed=market 일 때). selling 라벨은 거래유형 따라 분기 */}
      {isLoggedIn && feed === "market" && (
        <div className="px-4 mt-1.5 flex gap-1.5">
          {[
            { key: "all" as const, label: "전체" },
            { key: "selling" as const, label: marketTrade === "buy" ? "구매중" : marketTrade === "sell" ? "판매중" : "거래중" },
            { key: "reserved" as const, label: "예약중" },
            { key: "sold" as const, label: "거래완료" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setMarketStatus(t.key)}
              className={`text-2xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                marketStatus === t.key ? "bg-primary/10 border-primary text-primary" : "border-border text-text-sub"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* 검색 */}
      <form onSubmit={submitSearch} className="px-4 mt-3">
        <div className="relative">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="키워드 · 본문 검색"
            className="w-full bg-bg-secondary text-text text-sm pl-10 pr-3 py-2.5 rounded-full border border-border focus:border-primary/50 outline-none"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub text-sm">🔍</span>
        </div>
      </form>

      {/* 통합 글 종류 필터 — post_type + content_category 한 줄 (사용자 피드백: 단순화). 장터 피드에선 무관하므로 숨김 */}
      {feed !== "market" && (
      <div ref={filterChipsRef} className="px-4 mt-4 flex gap-1.5 overflow-x-auto hide-scrollbar">
        {/* 전체 */}
        <button
          onClick={() => { setPostType("all"); setContentCategory(null); }}
          className={`text-2xs px-2.5 py-1.5 rounded-full border whitespace-nowrap font-semibold transition-colors ${
            postType === "all" && !contentCategory
              ? "bg-primary text-bg border-primary"
              : "border-border text-text-sub hover:text-text"
          }`}
        >
          전체
        </button>
        {/* 찬반 — 사용자 의도로 앞쪽 배치 */}
        <button
          onClick={() => { setPostType(postType === "poll" ? "all" : "poll"); setContentCategory(null); }}
          className={`text-2xs px-2.5 py-1.5 rounded-full border whitespace-nowrap font-semibold transition-colors ${
            postType === "poll"
              ? "bg-primary text-bg border-primary"
              : "border-border text-text-sub hover:text-text"
          }`}
        >
          찬반
        </button>
        {/* 태그글 */}
        <button
          onClick={() => { setPostType(postType === "item_tagged" ? "all" : "item_tagged"); setContentCategory(null); }}
          className={`text-2xs px-2.5 py-1.5 rounded-full border whitespace-nowrap font-semibold transition-colors ${
            postType === "item_tagged"
              ? "bg-primary text-bg border-primary"
              : "border-border text-text-sub hover:text-text"
          }`}
        >
          태그글
        </button>
        {/* 자유글 */}
        <button
          onClick={() => { setPostType(postType === "free" ? "all" : "free"); setContentCategory(null); }}
          className={`text-2xs px-2.5 py-1.5 rounded-full border whitespace-nowrap font-semibold transition-colors ${
            postType === "free"
              ? "bg-primary text-bg border-primary"
              : "border-border text-text-sub hover:text-text"
          }`}
        >
          자유글
        </button>
        {/* 글 내용 카테고리 (마이그 036, 6종) */}
        {contentCats.map((c) => (
          <button
            key={c.key}
            onClick={() => {
              setContentCategory(contentCategory === c.key ? null : c.key);
              setPostType("all");
            }}
            title={c.desc}
            className={`text-2xs px-2.5 py-1.5 rounded-full border whitespace-nowrap font-semibold transition-colors ${
              contentCategory === c.key
                ? "bg-primary text-bg border-primary"
                : "border-border text-text-sub hover:text-text"
            }`}
          >
            {c.label}{c.count > 0 ? ` ${c.count}` : ""}
          </button>
        ))}
      </div>
      )}

      {/* 찬반 sub 필터 — postType='poll' 시만 노출 (진행중 / 종료) 2026-05-15 */}
      {postType === "poll" && (
        <div className="px-4 mt-2 flex gap-1.5">
          {([
            { key: "all" as const, label: "전체" },
            { key: "open" as const, label: "진행중" },
            { key: "closed" as const, label: "종료" },
          ]).map((opt) => (
            <button
              key={opt.key}
              onClick={() => setPollStatus(opt.key)}
              className={`text-2xs px-2.5 py-1 rounded-full border transition-colors ${
                pollStatus === opt.key
                  ? "bg-primary/20 text-primary border-primary/50 font-bold"
                  : "border-border text-text-sub"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* R3-33 S4-D — 지역 필터칩 (있을 때만 노출) */}
      {regions.length > 0 && (
        <div ref={regionChipsRef} className="px-4 mt-2 flex gap-1.5 overflow-x-auto hide-scrollbar">
          <button
            onClick={() => setRegion(null)}
            className={`text-2xs px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors ${
              region === null ? "bg-primary text-bg border-primary" : "border-border text-text-sub"
            }`}
          >
            전국
          </button>
          {regions.map((r) => (
            <button
              key={r.region}
              onClick={() => setRegion(region === r.region ? null : r.region)}
              className={`text-2xs px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors ${
                region === r.region ? "bg-primary text-bg border-primary" : "border-border text-text-sub"
              }`}
            >
              {r.region} {r.count}
            </button>
          ))}
        </div>
      )}

      {/* 정렬 + 인사이트만 토글 */}
      <div className="px-4 mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next: SortKey = sort === "latest" ? "popular" : sort === "popular" ? "view" : "latest";
              setSort(next);
            }}
            className="text-xs font-semibold text-text-sub hover:text-text"
          >
            {sort === "latest" ? "최신순 ▾" : sort === "popular" ? "인기순 ▾" : "조회순 ▾"}
          </button>
          {feed !== "market" && (
            <button
              onClick={() => setHasInsight(!hasInsight)}
              className={`text-2xs px-2 py-1 rounded-full border transition-colors ${
                hasInsight ? "bg-primary/20 border-primary/50 text-primary font-bold" : "border-border text-text-sub"
              }`}
            >
              매출 변화 후기만
            </button>
          )}
        </div>
        {q && (
          <button
            onClick={() => {
              setSearchInput("");
              setQ("");
            }}
            className="text-xs text-primary"
          >
            검색 해제 ×
          </button>
        )}
      </div>

      {/* 피드 리스트 */}
      <div className="px-4 mt-3">
        {loading ? (
          <p className="text-text-sub text-center py-8">불러오는 중...</p>
        ) : !posts || posts.length === 0 ? (
          <div className="py-12 text-center">
            {feed === "friends" ? (
              <>
                <p className="text-3xl mb-3">👥</p>
                <p className="text-text-sub text-sm">아직 친구가 없어요</p>
                <p className="text-text-sub text-xs mt-1">친구를 추가하면 그들의 글이 여기 모여요</p>
                <Link href="/mypage/friends" className="btn-primary text-xs px-4 py-2 mt-4 inline-block">
                  친구 추가하러 가기
                </Link>
              </>
            ) : feed === "mentions" ? (
              <>
                <p className="text-3xl mb-3">@</p>
                <p className="text-text-sub text-sm">받은 멘션이 없어요</p>
                <p className="text-text-sub text-xs mt-1">누가 @멘션하면 여기에 모여요</p>
              </>
            ) : (
              <>
                <p className="text-text-sub text-sm">
                  {q ? "검색 결과가 없어요" : "아직 등록된 게시물이 없어요"}
                </p>
                {hasHydrated && isLoggedIn && !q && (
                  <p className="text-text-sub text-xs mt-2">오른쪽 하단 ✏️ 버튼으로 첫 후기를 남겨보세요</p>
                )}
              </>
            )}
          </div>
        ) : (
          <div>
            {/* 컬럼 헤더 제거 (2026-05-26 — 제목 노출 글자수 확보 위해 표형 → 2줄 카드형) */}
            <div className="border-t border-border" />
            {/* 네이버 카페 스타일 리스트 (마이그 101, 2026-05-25) */}
            <div>
              {posts
                .filter((p) => {
                  if (postType !== "poll" || pollStatus === "all") return true;
                  const closed = p.poll?.is_closed ?? false;
                  return pollStatus === "open" ? !closed : closed;
                })
                .map((p) => (
                  <PostRow
                    key={p.id}
                    post={p}
                    read={isRead(p.id)}
                    onClick={() => markRead(p.id)}
                    onBookmarkToggle={(newState) => {
                      setPosts((prev) => prev?.map((pp) => pp.id === p.id ? { ...pp, bookmarked: newState } : pp) ?? null);
                    }}
                  />
                ))}
            </div>

            {/* 무한스크롤 — 더 보기 */}
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full py-3 text-sm font-semibold text-text-sub border border-border rounded-lg hover:bg-bg-elevated disabled:opacity-50 mt-3"
              >
                {loadingMore ? "불러오는 중..." : "더 보기"}
              </button>
            )}

            {/* 페이지 번호 (하이브리드) — 깊은 페이지 직접 점프 */}
            {totalCount > PAGE_SIZE && (
              <Pagination
                page={page}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={gotoPage}
                loading={loadingMore}
              />
            )}

            {!hasMore && posts.length > PAGE_SIZE && page * PAGE_SIZE >= totalCount && (
              <p className="text-2xs text-text-sub text-center pt-2">이게 전부예요 🎉</p>
            )}
          </div>
        )}
      </div>

      {/* 글쓰기 플로팅 버튼 (로그인 시만, hydration 후) — 우측 복귀 (사용자 결정 2026-05-08, AI 챗봇 floating 제거됨) */}
      {hasHydrated && isLoggedIn && (
        <Link
          href={feed === "market" ? "/community/new?type=market" : "/community/new"}
          className="fixed bottom-24 right-5 z-30 px-4 h-12 rounded-full bg-primary text-bg font-bold flex items-center gap-1.5 shadow-lg hover:scale-105 transition-transform"
          aria-label="글쓰기"
        >
          <span className="text-base">{feed === "market" ? "🛒" : "✏️"}</span>
          <span className="text-sm">{feed === "market" ? "장터 글쓰기" : "글쓰기"}</span>
        </Link>
      )}
    </div>
  );
}

/* ──────────────────────────────────────
   서브 컴포넌트
   ────────────────────────────────────── */

function TopicBadge({ post }: { post: CommunityPostOut }) {
  if (post.post_type === "item_tagged" && post.keyword) {
    return (
      <span className="text-2xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0">
        #{post.keyword}
      </span>
    );
  }
  if (post.post_type === "poll") {
    return (
      <span className="text-2xs font-bold px-2 py-0.5 rounded-full bg-yellow-400/10 text-yellow-400 shrink-0">
        찬반
      </span>
    );
  }
  // free 글
  return (
    <span className="text-2xs font-bold px-2 py-0.5 rounded-full bg-bg-elevated text-text-sub shrink-0">
      자유글
    </span>
  );
}

function PollPreview({ poll }: { poll: NonNullable<CommunityPostOut["poll"]> }) {
  if (poll.total === 0) {
    return (
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-2xs text-text-sub">아직 투표 없음 — 첫 의견 주세요</p>
        {poll.is_closed && (
          <span className="text-2xs font-bold text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded shrink-0">종료</span>
        )}
      </div>
    );
  }
  const proPct = Math.round((poll.pro_count / poll.total) * 100);
  return (
    <div className="mt-2">
      {/* 종료 상태 — 자물쇠 → 명확한 'X 찬반 종료' 라벨 (2026-05-15 사용자 결정) */}
      {poll.is_closed && (
        <div className="mb-1.5 flex items-center gap-1.5">
          <span className="text-2xs font-bold text-red-400 bg-red-400/10 px-2 py-0.5 rounded">찬반 종료</span>
          <span className="text-2xs text-text-sub">최종 {poll.total}명 참여</span>
        </div>
      )}
      <div className="flex items-center gap-2 text-2xs">
        <div className={`flex-1 bg-bg-elevated rounded-full h-1.5 overflow-hidden flex ${poll.is_closed ? "opacity-60" : ""}`}>
          <div className="bg-primary" style={{ width: `${proPct}%` }} />
          <div className="bg-red-400" style={{ width: `${100 - proPct}%` }} />
        </div>
        <span className="text-text-sub shrink-0">
          {proPct}% / {100 - proPct}% · {poll.total}명
        </span>
      </div>
    </div>
  );
}

function MetaBadges({ post }: { post: CommunityPostOut }) {
  return (
    <>
      {post.region && (
        <span className="text-2xs text-text-sub bg-bg-elevated px-1.5 py-0.5 rounded shrink-0">
          {post.region}
        </span>
      )}
      {post.category && (
        <span className="text-2xs text-text-sub bg-bg-elevated px-1.5 py-0.5 rounded shrink-0">
          {post.category}
        </span>
      )}
    </>
  );
}

function InsightCard({ insight }: { insight: NonNullable<CommunityPostOut["insight"]> }) {
  const up = insight.change_pct !== null && insight.change_pct >= 0;
  return (
    <div className="mt-2 bg-primary/5 border border-primary/20 rounded p-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-2xs text-text-sub">📊 {insight.menu_name ?? "메뉴"} · {METRIC_LABEL[insight.metric_type] ?? insight.metric_type}</p>
          <p className="text-xs text-text-sub mt-0.5 truncate">
            {insight.metric_before} → {insight.metric_after}
          </p>
        </div>
        {insight.change_pct !== null && (
          <p className={`text-base font-black shrink-0 ${up ? "text-primary" : "text-red-400"}`}>
            {up ? "↑" : "↓"} {Math.abs(insight.change_pct)}%
          </p>
        )}
      </div>
    </div>
  );
}


/** 페이지네이션 컴포넌트 (1 2 3 ... N → 하이브리드) */
function Pagination({
  page, totalCount, pageSize, onPageChange, loading,
}: {
  page: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (target: number) => void;
  loading?: boolean;
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  if (totalPages <= 1) return null;

  // 보여줄 페이지 윈도우 — 최대 5개 (현재 페이지 중심)
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  const numbers: number[] = [];
  for (let i = start; i <= end; i++) numbers.push(i);

  return (
    <div className="mt-6 flex items-center justify-center gap-1 text-2xs">
      <button
        onClick={() => onPageChange(1)}
        disabled={page === 1 || loading}
        className="px-2 py-1.5 text-text-sub disabled:opacity-30 hover:text-text"
      >«</button>
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1 || loading}
        className="px-2 py-1.5 text-text-sub disabled:opacity-30 hover:text-text"
      >‹</button>
      {start > 1 && <span className="px-1 text-text-sub">...</span>}
      {numbers.map((n) => (
        <button
          key={n}
          onClick={() => onPageChange(n)}
          disabled={loading}
          className={`min-w-[28px] px-2 py-1.5 rounded transition-colors ${
            n === page
              ? "bg-primary text-bg font-bold"
              : "text-text-sub hover:bg-bg-elevated hover:text-text"
          }`}
        >{n}</button>
      ))}
      {end < totalPages && <span className="px-1 text-text-sub">...</span>}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages || loading}
        className="px-2 py-1.5 text-text-sub disabled:opacity-30 hover:text-text"
      >›</button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={page === totalPages || loading}
        className="px-2 py-1.5 text-text-sub disabled:opacity-30 hover:text-text"
      >»</button>
      <span className="ml-2 text-text-sub">{page} / {totalPages}</span>
    </div>
  );
}

function relativeTime(d: Date): string {
  const now = Date.now();
  const diff = Math.max(0, (now - d.getTime()) / 1000);
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return d.toLocaleDateString("ko-KR");
}
